import { jwtDecode } from "jwt-decode";

const IDENTITY_SECRET =
  process.env.REACT_APP_IDENTITY_SECRET_KEY ||
  process.env.REACT_APP_IDENTITY_ENCRYPTION_KEY ||
  "";

const bytesToBase64 = (bytes) => {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

const textToBytes = (text) => new TextEncoder().encode(text);

let keyPromise = null;

const getEncryptionKey = async () => {
  if (!IDENTITY_SECRET) return null;

  if (!keyPromise) {
    keyPromise = (async () => {
      const subtle = window.crypto?.subtle;
      if (!subtle) {
        console.warn("[identity] WebCrypto is not available");
        return null;
      }

      // Derive a stable 32-byte AES key from the env secret (any length passphrase).
      const digest = await subtle.digest("SHA-256", textToBytes(IDENTITY_SECRET));
      return subtle.importKey("raw", digest, { name: "AES-GCM" }, false, [
        "encrypt",
      ]);
    })().catch((err) => {
      console.warn("[identity] Failed to import encryption key:", err);
      keyPromise = null;
      return null;
    });
  }

  return keyPromise;
};

/**
 * Read user id + email from the auth JWT in localStorage.
 */
export const getIdentityUserFromToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return { id: "", email: "" };

    const decoded = jwtDecode(token);
    const id =
      decoded?.user_id ??
      decoded?.userId ??
      decoded?.id ??
      decoded?.sub ??
      decoded?.user?.id ??
      "";
    const email =
      decoded?.email ??
      decoded?.user_email ??
      decoded?.userEmail ??
      decoded?.user?.email ??
      "";

    return {
      id: id === undefined || id === null ? "" : String(id),
      email: email === undefined || email === null ? "" : String(email),
    };
  } catch {
    return { id: "", email: "" };
  }
};

/**
 * Encrypt `timestamp+id+email` with REACT_APP_IDENTITY_SECRET_KEY.
 * Returns `{ identity, timestamp }` where:
 * - identity  → base64(iv || ciphertext+tag)
 * - timestamp → same ms value embedded in the plaintext (sent as a separate header)
 * or null if key/user missing.
 */
export const buildEncryptedIdentityHeader = async () => {
  const { id, email } = getIdentityUserFromToken();

  // Skip when logged out / incomplete identity (login OTP calls, etc.)
  if (!id && !email) return null;

  const timestamp = Date.now();
  const plaintext = `${timestamp}+${id}+${email}`;
  const key = await getEncryptionKey();
  if (!key) {
    console.warn(
      "[identity] Missing REACT_APP_IDENTITY_SECRET_KEY — identity header skipped",
    );
    return null;
  }

  const subtle = window.crypto?.subtle;
  if (!subtle) return null;

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    textToBytes(plaintext),
  );

  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return {
    identity: bytesToBase64(combined),
    timestamp: String(timestamp),
  };
};
